// HU-23 · Formato, plazo y firma por autoridad. F-05, Sprint 2.
//
// Los 14 mercados se parametrizan SIN REDESPLEGAR (RNF-08, RNF-10): plantilla de carta,
// plazo para EA grave y no grave, unidad, umbral de alerta, origen del computo (RN-16) y
// si la autoridad exige la firma del Profesional Responsable (RN-05).
//
// ⚠ EL SISTEMA NO TRAE NINGUN PLAZO, Y ESTE MODULO TAMPOCO. RN-12: conviven la norma
// andina, la nacional de cada pais y el objetivo interno del corporativo, y solo ILENDER
// sabe cual aplica a cada autoridad. Aqui no hay ni una constante con un numero de dias:
// si la hubiera, seria una interpretacion juridica escrita en el front — el mismo defecto
// que ya se pago con `src/data/paises.js`, donde los 14 paises y su autoridad vivian en
// el bundle mientras la base tenia las tablas y nadie las leia.
//
// VA POR `/administracion` Y NO POR `/referencias`. Son las mismas dos tablas que sirve
// `listarPaises()`, y la diferencia es deliberada: aquel devuelve DOS columnas (autoridad
// y ruta de firma) porque el formulario de HU-01 las necesita para rotular el desplegable,
// y lo lee cualquier rol. Esto devuelve la configuracion regulatoria completa y la
// escribe, asi que exige rol `administrador` — el router entero del backend lo hace.

import { pedir, seg, segPais } from './cliente';
import type { RespuestaDelApi } from '../tipos';

/** Las tres unidades del CHECK `parametro_unidad_plazo_valida` (006). */
export const UNIDADES_DE_PLAZO = [
  { value: 'horas', label: 'Horas', nota: 'Horas de reloj, no hábiles. Colombia (72 h) es el caso de referencia.' },
  { value: 'dias_habiles', label: 'Días hábiles', nota: 'Descuenta fines de semana y los feriados de ESE país.' },
  { value: 'dias_corridos', label: 'Días corridos', nota: 'Días de calendario, feriados incluidos.' },
];

/**
 * RN-16, el punto abierto con mayor exposición regulatoria del proyecto.
 *
 * La Decisión Andina 960 cuenta desde la RECEPCIÓN; ILENDER sostiene que desde la
 * PRESENTACIÓN ante la autoridad. El sistema guarda las dos fechas y no elige: lo elige
 * esta configuración, por autoridad, con la recepción por defecto por ser el criterio de
 * la norma.
 */
export const ORIGENES_DE_COMPUTO = [
  // Las etiquetas son las que nombra HU-23-C3 (CP-23-01 del plan de pruebas): «Recepción
  // Ilender» y «Presentación Autoridad», no una paráfrasis. Solo cambia lo que se muestra:
  // el `value` es el código que viaja a la API y se guarda en `parametro_pais.origen_computo`.
  { value: 'recepcion_ilender', label: 'Recepción Ilender', nota: 'El «día cero» de la Res. 2528 Anexo I: la fecha en que ILENDER recibe el reporte, sea cual sea la fuente. Es el valor por defecto.' },
  { value: 'presentacion_autoridad', label: 'Presentación Autoridad', nota: 'Solo admisible donde una norma nacional desplace a la andina. Para una autoridad CAN, elegirlo es incumplimiento.' },
];

export function listarAutoridades() {
  return pedir<RespuestaDelApi[]>('/administracion/autoridades');
}

export function verAutoridad(pais: string) {
  return pedir(`/administracion/autoridades/${segPais(pais)}`);
}

/**
 * PUT: SUSTITUYE la configuración entera de la autoridad.
 *
 * ⚠ Omitir un plazo lo DEJA SIN CONFIGURAR; no lo conserva. Es lo que permite borrar un
 * plazo mal cargado desde la pantalla — con semántica de parche, dejar una autoridad sin
 * plazo (estado legítimo y hoy el más frecuente) exigiría un UPDATE a mano contra la
 * base. Por eso el formulario manda siempre el cuerpo completo.
 */
export function configurarAutoridad(pais: string, configuracion: Record<string, unknown>) {
  return pedir(`/administracion/autoridades/${segPais(pais)}`, {
    metodo: 'PUT',
    cuerpo: configuracion,
  });
}

/**
 * El calendario hábil de un país (RN-13).
 *
 * Un país sin feriados responde 200 con la lista vacía, no 404: lo que pasa no es que el
 * país no exista sino que su calendario no está cargado, y ese hueco HU-23 manda
 * señalarlo. `anios_cargados` viene completo aunque se filtre por año.
 */
export function listarFeriados(pais: string, anio?: number | string) {
  const consulta = anio ? `?anio=${encodeURIComponent(anio)}` : '';
  return pedir(`/administracion/feriados/${segPais(pais)}${consulta}`);
}

/**
 * Carga acumulativa e idempotente por (país, fecha). NO borra lo que ya había.
 *
 * `fuente` y `consultado_el` son obligatorios y el backend los exige: un calendario de
 * feriados decide si una notificación llegó a tiempo ante la autoridad, y en una
 * inspección la pregunta no es qué fechas se cargaron sino de dónde salieron.
 */
export function cargarFeriados(pais: string, carga: Record<string, unknown>) {
  return pedir<RespuestaDelApi>(`/administracion/feriados/${segPais(pais)}`, {
    metodo: 'POST',
    cuerpo: carga,
  });
}

/**
 * PUT: los datos con los que la autoridad se IDENTIFICA en el Formato Amarillo.
 *
 * ⚠ ES OTRO ENDPOINT Y NO UN CAMPO MÁS DEL PUT DE ARRIBA, Y LA SEPARACIÓN IMPORTA.
 * Aquel sustituye la configuración entera: si el contacto viajara en él, esta pantalla
 * —que no muestra ni un plazo— tendría que reenviarlos todos para no borrarlos, y el día
 * que alguien olvidara uno una autoridad se quedaría sin plazo regulatorio. Lo fija
 * `tests/integracion/test_api_autoridades.py` contra Postgres real.
 *
 * El logo viaja INCRUSTADO (`data:image/...;base64,...`) y no como una dirección: el
 * Formato Amarillo se imprime desde un HTML que arma el servidor, y un `<img>` que
 * apunte a otro sitio puede llegar vacío justo en el documento que importa.
 */
export function configurarContactoDeAutoridad(pais: string, contacto: Record<string, unknown>) {
  return pedir<RespuestaDelApi>(`/administracion/autoridades/${segPais(pais)}/contacto`, {
    metodo: 'PUT',
    cuerpo: contacto,
  });
}

// ---------------------------------------------------------------------------
// CC-02 (B2) · varias autoridades por país (AT S3 §11.3.1, rutas 66 a 69)
// ---------------------------------------------------------------------------
//
// Las rutas de arriba por `{pais}` siguen funcionando como ALIAS de la autoridad
// predeterminada del país. Estas van por `{pais}/{codigo}`. No hay borrado: una autoridad
// se desactiva (RN-72).

/** Los actos sobre una autoridad, con el segmento de la ruta que los nombra. */
export const ACTOS_SOBRE_LA_AUTORIDAD = {
  desactivar: 'desactivacion',
  reactivar: 'reactivacion',
  marcar_predeterminada: 'predeterminada',
  confirmar: 'confirmacion',
} as const;

export type ActoSobreLaAutoridad = keyof typeof ACTOS_SOBRE_LA_AUTORIDAD;

/** Alta: código y nombre. Nace sin plazos y con la firma de la predeterminada del país. */
export function darDeAltaAutoridad(pais: string, alta: { codigo: string; nombre: string }) {
  return pedir<RespuestaDelApi>(`/administracion/autoridades/${segPais(pais)}`, {
    metodo: 'POST',
    cuerpo: alta,
  });
}

/** El detalle de una autoridad, con su logo (la lista no lo trae). */
export function verAutoridadDelPais(pais: string, codigo: string) {
  return pedir<RespuestaDelApi>(`/administracion/autoridades/${segPais(pais)}/${seg(codigo)}`);
}

/** PUT: SUSTITUYE la configuración de una autoridad (todo menos el código). */
export function configurarAutoridadDelPais(pais: string, codigo: string, configuracion: Record<string, unknown>) {
  return pedir<RespuestaDelApi>(`/administracion/autoridades/${segPais(pais)}/${seg(codigo)}`, {
    metodo: 'PUT',
    cuerpo: configuracion,
  });
}

/** PUT: el encabezado de la AC de una autoridad. Ningún plazo. */
export function configurarContactoDeAutoridadDelPais(pais: string, codigo: string, contacto: Record<string, unknown>) {
  return pedir<RespuestaDelApi>(`/administracion/autoridades/${segPais(pais)}/${seg(codigo)}/contacto`, {
    metodo: 'PUT',
    cuerpo: contacto,
  });
}

/** Desactivar, reactivar, marcar como predeterminada o confirmar. */
export function cambiarEstadoDeAutoridad(pais: string, codigo: string, acto: ActoSobreLaAutoridad) {
  return pedir<RespuestaDelApi>(
    `/administracion/autoridades/${segPais(pais)}/${seg(codigo)}/${ACTOS_SOBRE_LA_AUTORIDAD[acto]}`,
    { metodo: 'POST' },
  );
}
